import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  BellIcon, 
  MegaphoneIcon, 
  CalendarIcon, 
  StarIcon, 
  ChatBubbleLeftIcon,
  HeartIcon,
  HandThumbUpIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserGroupIcon,
  PlusIcon,
  FunnelIcon,
  BookOpenIcon,
  SparklesIcon,
  RocketLaunchIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { useFeed } from '../hooks/useFeed';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '../hooks/useUsers';
import { FeedPost, ClassSchedule } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import CreatePostModal from '../components/CreatePostModal';
import PostCard from '../components/PostCard';
import ClassScheduleCard from '../components/ClassScheduleCard';
import AIChatButton from '../components/AIChatButton';

const Feed: React.FC = React.memo(() => {
  const { 
    posts, 
    schedules, 
    loading, 
    getPostsByCategory, 
    getPinnedPosts, 
    getUnreadCount,
    getUpcomingClasses
  } = useFeed();
  const { currentUser } = useAuth();
  const { fetchUsers } = useUsers();
  
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState<'feed' | 'schedule'>('feed');
  const [searchTerm, setSearchTerm] = useState('');

  // Memoize categories to prevent recreation
  const categories = useMemo(() => [
    { id: 'all', name: 'All Posts', icon: MegaphoneIcon, color: 'bg-blue-500' },
    { id: 'ICAG', name: 'ICAG', icon: UserGroupIcon, color: 'bg-green-500' },
    { id: 'CITG', name: 'CITG', icon: UserGroupIcon, color: 'bg-purple-500' },
    { id: 'Important', name: 'Important', icon: ExclamationTriangleIcon, color: 'bg-red-500' },
    { id: 'Class Schedule', name: 'Class Schedule', icon: CalendarIcon, color: 'bg-yellow-500' },
    { id: 'General', name: 'General', icon: ChatBubbleLeftIcon, color: 'bg-gray-500' }
  ], []);

  // Memoize computed values to prevent recalculation
  const pinnedPosts = useMemo(() => getPinnedPosts(), [getPinnedPosts, posts]);
  const upcomingClasses = useMemo(() => getUpcomingClasses(), [getUpcomingClasses, schedules]);
  const unreadCount = useMemo(() => getUnreadCount(), [getUnreadCount, posts, currentUser]);

  // Memoize feed stats
  const feedStats = useMemo(() => {
    return {
      totalPosts: posts.length,
      totalSchedules: schedules.length,
      pinnedPosts: pinnedPosts.length,
      upcomingClasses: upcomingClasses.length
    };
  }, [posts.length, schedules.length, pinnedPosts.length, upcomingClasses.length]);

  const filteredPosts = useMemo(() => {
    return selectedCategory === 'all' 
      ? posts 
      : getPostsByCategory(selectedCategory);
  }, [selectedCategory, posts, getPostsByCategory]);

  const filteredSchedules = useMemo(() => {
    return selectedCategory === 'all' || selectedCategory === 'Class Schedule'
      ? schedules
      : schedules.filter(schedule => schedule.category === selectedCategory);
  }, [selectedCategory, schedules]);

  // Allow authenticated users to create posts, but not pending authors
  const canCreatePosts = useMemo(() => {
    return !!currentUser && currentUser.status !== 'pending';
  }, [currentUser]);

  // Fetch user data for all post authors - only when posts change
  useEffect(() => {
    if (posts.length > 0) {
      const authorIds = posts.map(post => post.authorId).filter(Boolean);
      if (authorIds.length > 0) {
        fetchUsers(authorIds);
      }
    }
  }, [posts, fetchUsers]);

  // Memoize event handlers
  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category);
  }, []);

  const handleViewModeChange = useCallback((mode: 'feed' | 'schedule') => {
    setViewMode(mode);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            
            {/* Left Side - Text Content */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Welcome Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <MegaphoneIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Community Updates
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Stay Connected</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  With Your Community
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Get the latest updates, announcements, and class schedules from your learning community. Never miss important information.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {feedStats.totalPosts}
                  </div>
                  <div className="text-xs text-blue-200">Total Posts</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {feedStats.pinnedPosts}
                  </div>
                  <div className="text-xs text-blue-200">Pinned</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {feedStats.totalSchedules}
                  </div>
                  <div className="text-xs text-blue-200">Classes</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {feedStats.upcomingClasses}
                  </div>
                  <div className="text-xs text-blue-200">Upcoming</div>
                </div>
              </div>

              {/* Call-to-Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={() => {
                    const searchInput = document.querySelector('input[type="text"]') as HTMLInputElement;
                    searchInput?.focus();
                  }}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm"
                >
                  <RocketLaunchIcon className="h-4 w-4 mr-1.5" />
                  Search Feed
                </button>
                {canCreatePosts ? (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                  >
                    <PlusIcon className="h-4 w-4 mr-1.5" />
                    Create Post
                  </button>
                ) : (
                  <Link
                    to="/signup"
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                  >
                    <SparklesIcon className="h-4 w-4 mr-1.5" />
                    Join Community
                  </Link>
                )}
              </div>
            </div>

            {/* Right Side - Visual Elements - Hidden on mobile for stability */}
            <div className="relative hidden xl:block">
              {/* Feed Activity Cards */}
              <div className="space-y-4">
                {/* Recent Posts Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <MegaphoneIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Recent Activity</h3>
                      <p className="text-blue-200 text-sm">{feedStats.totalPosts} posts shared</p>
                    </div>
                  </div>
                </div>

                {/* Class Schedule Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <CalendarIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Class Schedule</h3>
                      <p className="text-blue-200 text-sm">{feedStats.upcomingClasses} upcoming classes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-0 sm:pt-6 relative z-20 space-y-4 sm:space-y-8">
        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6 px-3 sm:px-0">
          {/* View Mode Toggle */}
          <div className="flex items-center justify-center">
            <div className="bg-white rounded-xl p-1 shadow-sm border border-gray-200 max-w-full">
              <button
                onClick={() => setViewMode('feed')}
                className={`px-3 sm:px-6 py-2 rounded-lg font-medium transition-all max-w-full ${
                  viewMode === 'feed'
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MegaphoneIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-1 sm:mr-2" />
                <span className="text-sm sm:text-base">Feed Posts</span>
              </button>
              <button
                onClick={() => setViewMode('schedule')}
                className={`px-3 sm:px-6 py-2 rounded-lg font-medium transition-all max-w-full ${
                  viewMode === 'schedule'
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-1 sm:mr-2" />
                <span className="text-sm sm:text-base">Class Schedule</span>
              </button>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="relative flex-1 sm:flex-none sm:w-80">
            <input
              type="text"
              placeholder="Search posts and schedules..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full px-3 py-2 pl-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <svg
              className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="text-sm text-gray-600">
              {feedStats.totalPosts} post{feedStats.totalPosts !== 1 ? 's' : ''}
            </div>
            {canCreatePosts && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Post
              </button>
            )}
          </div>
        </div>

        {/* Category Filter */}
        <div className="px-3 sm:px-0">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Filter by Category</h2>
            <FunnelIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="flex flex-wrap gap-1 sm:gap-2 lg:gap-3 max-w-full">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-all max-w-full text-xs sm:text-sm ${
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <category.icon className={`h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 ${selectedCategory === category.id ? 'text-white' : category.color}`} />
                <span className="whitespace-nowrap">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Pinned Posts Section */}
        {pinnedPosts.length > 0 && viewMode === 'feed' && (
          <div className="px-3 sm:px-0">
            <h2 className="text-xl font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <StarIcon className="h-6 w-6 mr-2 text-yellow-500" />
              Pinned Posts
            </h2>
            <div className="grid gap-4 max-w-full">
              {pinnedPosts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          </div>
        )}

        {/* Welcome Message for New Users */}
        {!currentUser && posts.length === 0 && viewMode === 'feed' && (
          <div className="text-center py-8 sm:py-12 px-3 sm:px-0">
            <MegaphoneIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Welcome to the General Feed!</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              This is where you'll find all the latest updates, announcements, and class schedules from our community.
            </p>
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 max-w-full">
              <Link
                to="/signup"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
              >
                Join Our Community
              </Link>
              <Link
                to="/signin"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        )}

        {/* Upcoming Classes Section */}
        {upcomingClasses.length > 0 && (
          <div className="px-3 sm:px-0">
            <h2 className="text-xl font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <ClockIcon className="h-6 w-6 mr-2 text-orange-500" />
              Upcoming Classes (Next 24 Hours)
            </h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 max-w-full">
              {upcomingClasses.map((schedule) => (
                <ClassScheduleCard key={schedule.id} schedule={schedule} />
              ))}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="space-y-4 sm:space-y-6 px-3 sm:px-0">
          {viewMode === 'feed' ? (
            // Feed Posts
            <>
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedCategory === 'all' ? 'All Posts' : `${selectedCategory} Posts`}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({filteredPosts.length} posts)
                </span>
              </h2>
              
              {filteredPosts.length === 0 ? (
                <div className="text-center py-12 sm:py-16 max-w-full">
                  <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <MegaphoneIcon className="h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No posts found</h3>
                  <p className="text-gray-600 mb-6">
                    {selectedCategory === 'all' 
                      ? 'No posts have been created yet.' 
                      : `No posts found in the ${selectedCategory} category.`
                    }
                  </p>
                  {canCreatePosts && (
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Create the first post
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid gap-4 sm:gap-6 max-w-full">
                  {filteredPosts.map((post) => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </>
          ) : (
            // Class Schedule
            <>
              <h2 className="text-xl font-semibold text-gray-900">
                Class Schedule
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({filteredSchedules.length} classes)
                </span>
              </h2>
              
              {filteredSchedules.length === 0 ? (
                <div className="text-center py-12 sm:py-16 max-w-full">
                  <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <CalendarIcon className="h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No classes scheduled</h3>
                  <p className="text-gray-600">
                    No classes are currently scheduled.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 max-w-full">
                  {filteredSchedules.map((schedule) => (
                    <ClassScheduleCard key={schedule.id} schedule={schedule} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Post Modal */}
      {showCreateModal && (
        <CreatePostModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
});

export default Feed;
